import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, CheckCircle, Mail, Building2, Calendar, Loader2, AlertTriangle, Sparkles, Users } from "lucide-react";
import { GaMark } from "@/lib/quoting/GaMark";
import { findParameterGaps, countGaps } from "@/lib/quoting/rfpParameterGaps";
import { buildRfpRecipients, type RfpEmailRecipient } from "@/lib/quoting/rfpRecipients";
import {
  pickedCarrierIds,
  carrierIdsForGeneralAgency,
  carrierNamesFor,
  asksForGeneralAgency,
  isAgencyCopy,
} from "@shared/utils/rfpGaCarriers";
import { messageFor } from "@shared/utils/rfpEmailContent";
import { Link } from "wouter";
import type { Carrier, DirectoryContact } from "@shared/schema";
import { ConsolidatedRFPEmail } from "@/components/rfp/ConsolidatedRFPEmail";
import { RfpAttachments } from "./RfpAttachments";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface Step7RFPPreviewProps {
  onComplete?: () => void;
}

// Was declared here and again in ConsolidatedRFPEmail, neither copy carrying
// generalAgencyId, so the field buildRfpRecipients sets was invisible to both.
type AvailableContact = RfpEmailRecipient;

interface SentRecipient {
  name: string;
  email: string;
  carrierName: string;
  coverageTypes: string[];
  /** Which firm this went to, so the summary can group the way step 6 did. */
  generalAgencyId?: number | null;
  carrierId?: number | null;
  /** GA mode: what this firm was asked for, as it stood on the day. */
  carrierNames?: string[];
}

export function Step7RFPPreview({ onComplete }: Step7RFPPreviewProps = {}) {
  const { quoteData, updateQuoteData, carriers, directoryContacts, refetchDirectoryContacts, generalAgencies, isRFPMode, draftQuoteId } = useQuoteWizardContext();
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [customRecipients, setCustomRecipients] = useState<AvailableContact[]>([]);
  const [customMessage, setCustomMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  // The message editor is open in ConsolidatedRFPEmail. Send has to wait: it
  // rebuilds the email from the saved draft, so it would post the wording from
  // before the edit while the broker is still typing it.
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [rfpSent, setRfpSent] = useState(false);
  const [sentRecipients, setSentRecipients] = useState<SentRecipient[]>([]);
  const [sentDate, setSentDate] = useState<Date | null>(null);
  // Resolved after mount, so the footer div is guaranteed to be in the DOM.
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  useEffect(() => setFooterSlot(document.getElementById("wizard-step-footer")), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const rfpCoverageTypes = isRFPMode
    ? quoteData.coverageTypes
    : quoteData.coverageTypes.filter((type) =>
        ["dental", "vision", "life", "disability", "voluntary"].includes(type)
      );

  const usingGa = (quoteData.rfpSendVia ?? "carriers") === "ga";

  const parameterGaps = findParameterGaps(rfpCoverageTypes, quoteData.rfpParameters ?? {});
  const gapCount = countGaps(parameterGaps);

  // Who carriers will see this from, and where their replies land. The agency
  // must have provisioned its own sending address — we never send agency mail
  // from the platform address, because a carrier replying to noreply@ is a lost
  // response.
  const { user } = useAuth();
  const { data: emailSettings, isLoading: emailSettingsLoading } = useQuery<{
    fromEmail: string | null;
    fromName: string | null;
    replyToEmail: string | null;
    provisioningStatus: string | null;
    isActive: boolean | null;
  }>({ queryKey: ["/api/agency/email-settings"] });

  // The sign-off and the logo. Same endpoint the app header uses, which already
  // resolves an agency_member's details from the agency owner, and the same
  // fields the send resolves server-side.
  const { data: profile } = useQuery<{
    firstName?: string | null;
    lastName?: string | null;
    contactEmail?: string | null;
    email?: string | null;
    phone?: string | null;
    agencyName?: string | null;
    agencyLicenseNumber?: string | null;
    agencyLogo?: string | null;
  }>({ queryKey: ["/api/user/profile"] });

  const senderReady =
    !!emailSettings?.fromEmail && !!emailSettings.isActive && emailSettings.provisioningStatus === "provisioned";

  // The agency's configured reply-to wins — it's the address they deliberately
  // set in Settings for carrier replies. Falls back to the signed-in broker only
  // when the agency hasn't set one. Still editable per send.
  const [replyTo, setReplyTo] = useState("");
  useEffect(() => {
    if (replyTo) return;
    const preferred = emailSettings?.replyToEmail || (user as any)?.email || emailSettings?.fromEmail;
    if (preferred) setReplyTo(preferred);
  }, [user, emailSettings, replyTo]);

  const replyToValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyTo.trim());
  const [sendFailures, setSendFailures] = useState<{ email: string; error?: string }[]>([]);
  // Optional note for one carrier, replacing the shared message for them only.
  const [perRecipientMessages, setPerRecipientMessages] = useState<Record<string, string>>({});


  // Carriers chosen for this RFP in step 4 — they drive the email body, the
  // parameters step and the matching of proposals on the way back in, whichever
  // way the RFP is sent.
  const rfpCarriers = Array.from(
    new Set(rfpCoverageTypes.flatMap((type) => quoteData.selectedCarriers[type] ?? [])),
  )
    .map((id) => carriers.find((c: Carrier) => c.id === id))
    .filter((c): c is Carrier => !!c);

  // Who this actually gets emailed to: each carrier's own sales contact, or the
  // one or two general agents picked in step 4. See buildRfpRecipients for what
  // each mode means.
  const availableContacts: AvailableContact[] = buildRfpRecipients({
    sendVia: usingGa ? "ga" : "carriers",
    coverageTypes: rfpCoverageTypes,
    selectedCarriers: quoteData.selectedCarriers,
    selectedGeneralAgencyIds: quoteData.rfpGeneralAgencyIds ?? [],
    carriers,
    generalAgencies,
    directoryContacts,
  });

  // What each recipient's email will name, worked out exactly as the send
  // endpoint does it — same shared function, same inputs — so what the broker
  // approves here is what leaves. Undefined in carrier mode, which is what
  // keeps the carrier list out of a carrier's own email.
  const carrierNamesByRecipient = useMemo(() => {
    const links = (generalAgencies || []).map((generalAgency: any) => ({
      id: generalAgency.id,
      carrierIds: generalAgency.carrierIds ?? [],
    }));
    const picked = pickedCarrierIds(quoteData.selectedCarriers);
    const asks = quoteData.rfpGeneralAgencyAsks ?? [];
    const byId = new Map<string, string[] | undefined>();
    for (const contact of [...availableContacts, ...customRecipients]) {
      byId.set(
        contact.id,
        usingGa
          ? carrierNamesFor(
              // What the broker asked this firm for in step 4. A recipient typed
              // in here by hand belongs to no firm and gets the whole list, which
              // is what carrierIdsForGeneralAgency returns for a missing id.
              contact.generalAgencyId
                ? asksForGeneralAgency(asks, contact.generalAgencyId)
                : carrierIdsForGeneralAgency(undefined, picked, links),
              carriers,
            )
          : undefined,
      );
    }
    return byId;
  }, [availableContacts, customRecipients, generalAgencies, quoteData.selectedCarriers, carriers, usingGa]);

  // Selected carriers with nobody to email — based on who is actually TICKED,
  // not merely on file. Removing a carrier's last recipient puts it back in the
  // fill-in list below, so the gap is visible again rather than silently leaving
  // that carrier out of the RFP.
  const carriersWithoutContacts = usingGa ? [] : rfpCarriers.filter(
    (carrier) =>
      !availableContacts.some(
        (contact) => contact.carrierId === carrier.id && selectedContactIds.has(contact.id),
      ) && !customRecipients.some((recipient) => recipient.carrierId === carrier.id),
  );

  useEffect(() => {
    if (availableContacts.length > 0) {
      setSelectedContactIds((prev) => {
        if (prev.size === 0) {
          return new Set(availableContacts.map((ac) => ac.id));
        }
        const newSet = new Set(prev);
        availableContacts.forEach((ac) => {
          if (!prev.has(ac.id)) {
            newSet.add(ac.id);
          }
        });
        return newSet;
      });
    }
  }, [availableContacts.length]);

  const handleToggleContact = (contactId: string) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const handleAddCustomRecipient = (newRecipient: {
    name: string;
    email: string;
    carrierId?: number;
    carrierName?: string;
    directoryContactId?: number;
  }) => {
    const id = `custom-${Date.now()}`;
    setCustomRecipients((prev) => [
      ...prev,
      {
        id,
        directoryContactId: newRecipient.directoryContactId,
        name: newRecipient.name,
        email: newRecipient.email,
        carrierId: newRecipient.carrierId,
        carrierName: newRecipient.carrierName || "Other",
        coverageTypes: rfpCoverageTypes,
      },
    ]);
  };

  // A contact added inline is held here until the directory refetch brings it
  // back through availableContacts. Drop the local copy at that point so the
  // same person isn't listed — and emailed — twice.
  const visibleCustomRecipients = customRecipients.filter(
    (custom) => !availableContacts.some((contact) => contact.email.toLowerCase() === custom.email.toLowerCase()),
  );

  const handleRemoveCustomRecipient = (id: string) => {
    setCustomRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  /**
   * One copy to the broker's own inbox. Saves the draft first, for the same
   * reason the real send does: the server builds the email from wizardState,
   * so an unsaved edit would be missing from the test and present in the send.
   */
  const [sendingTest, setSendingTest] = useState(false);
  const handleSendTest = async () => {
    const recipients = [
      ...availableContacts.filter((c) => selectedContactIds.has(c.id)),
      ...visibleCustomRecipients,
    ];
    setSendingTest(true);
    try {
      if (draftQuoteId) {
        await apiRequest("PUT", `/api/quotes/${draftQuoteId}`, {
          type: "rfp",
          wizardState: { ...quoteData },
        });
      }
      const res = await apiRequest("POST", `/api/rfps/${draftQuoteId}/send`, {
        testOnly: true,
        recipients: recipients.map((r) => ({
          name: r.name,
          email: r.email,
          carrierName: r.carrierName,
          coverageTypes: r.coverageTypes,
          carrierId: r.carrierId,
          generalAgencyId: r.generalAgencyId,
          customMessage: messageFor(r.id, customMessage, perRecipientMessages),
        })),
        customMessage,
        replyTo,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message ?? "Could not send the test");
      toast({
        title: "Test sent",
        description: `A copy of ${body.previewOf}'s email is on its way to ${body.to}.`,
      });
    } catch (error: any) {
      toast({
        title: "Could not send the test",
        description: error?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendRFP = async () => {
    const selectedRecipients = availableContacts.filter((c) => selectedContactIds.has(c.id));
    const allRecipients = [...selectedRecipients, ...visibleCustomRecipients];

    if (allRecipients.length === 0) {
      toast({
        title: "No Recipients",
        description: "Please select at least one recipient to send the RFP.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      const now = new Date();

      const uniqueCarrierIds = Array.from(new Set(allRecipients.map(r => r.carrierId).filter((id): id is number => id !== undefined)));

      const rfpSentData = {
        sentAt: now.toISOString(),
        recipients: allRecipients.map(r => ({
          name: r.name,
          email: r.email,
          carrierName: r.carrierName,
          coverageTypes: r.coverageTypes,
        })),
      };

      if (draftQuoteId) {
        // Save the wizard state first so the send endpoint reads the parameters
        // the broker just previewed, then actually send.
        await apiRequest("PUT", `/api/quotes/${draftQuoteId}`, {
          type: "rfp",
          carrierIds: uniqueCarrierIds,
          wizardState: { ...quoteData, rfpSentData },
        });
      }

      const res = await apiRequest("POST", `/api/rfps/${draftQuoteId}/send`, {
        recipients: allRecipients.map((r) => ({
          name: r.name,
          email: r.email,
          carrierName: r.carrierName,
          coverageTypes: r.coverageTypes,
          carrierId: r.carrierId,
          // Says WHICH GA this person works for, nothing more. The server still
          // derives the carrier list itself; it will not take one from here.
          generalAgencyId: r.generalAgencyId,
          customMessage: messageFor(r.id, customMessage, perRecipientMessages),
        })),
        customMessage,
        replyTo,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message ?? "Failed to send the RFP");

      const failed: { email: string; error?: string }[] = (body.results ?? []).filter((r: any) => !r.success);
      const deliveredEmails = new Set(
        (body.results ?? []).filter((r: any) => r.success).map((r: any) => r.email),
      );

      // Only report as sent what actually went out.
      setSentRecipients(
        allRecipients
          .filter((r) => deliveredEmails.has(r.email))
          .map((r) => ({
            name: r.name,
            email: r.email,
            carrierName: r.carrierName,
            coverageTypes: r.coverageTypes,
            generalAgencyId: r.generalAgencyId ?? null,
            carrierId: r.carrierId ?? null,
            carrierNames: carrierNamesByRecipient.get(r.id) ?? [],
          })),
      );
      setSentDate(now);
      setSendFailures(failed);
      setRfpSent(true);

      if (failed.length) {
        toast({
          title: `${body.sent} sent, ${failed.length} failed`,
          description: failed.map((f) => `${f.email}: ${f.error ?? "unknown error"}`).join("; "),
          variant: "destructive",
        });
      } else {
        toast({
          title: "RFP sent",
          description: `Sent to ${body.sent} recipient${body.sent === 1 ? "" : "s"} from ${body.from}.`,
        });
      }
    } catch (error: any) {
      console.error("Failed to send RFP:", error);
      toast({
        title: "Failed to Send RFP",
        description: error?.message ?? "There was an error sending the RFP. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const selectedCount = availableContacts.filter((c) => selectedContactIds.has(c.id)).length;
  const totalRecipients = selectedCount + visibleCustomRecipients.length;
  // Colleagues are CC'd on the carrier emails, so on their own they are nobody
  // to send to. The server refuses this too; catching it here explains why.
  const hasMarketRecipient = [
    ...availableContacts.filter((c) => selectedContactIds.has(c.id)),
    ...visibleCustomRecipients,
  ].some((recipient) => !isAgencyCopy(recipient));
  const blockingReason = isEditingMessage
    ? "Finish editing the message"
    : totalRecipients === 0
      ? "Pick at least one recipient"
      : !hasMarketRecipient
        ? "Everyone picked is on your team. Pick a carrier or general agency contact."
        : !senderReady
          ? "Your agency's sending address isn't set up"
          : !replyToValid
            ? "Enter a valid reply-to address"
            : "";
  const readyToSend = !blockingReason;

  const getUniqueCarriers = () => {
    const carrierSet = new Set<string>();
    sentRecipients.forEach(r => carrierSet.add(r.carrierName));
    return Array.from(carrierSet);
  };

  /**
   * What went out, by firm. The same shape the review step showed, so the
   * confirmation reads as the screen before it rather than a flat address list.
   */
  const sentByFirm = () => {
    const groups = new Map<
      string,
      { firmId: number | null; title: string; recipients: SentRecipient[]; carrierNames: string[] }
    >();
    for (const recipient of sentRecipients) {
      const firmId = recipient.generalAgencyId ?? recipient.carrierId ?? null;
      const key = firmId === null ? `other:${recipient.email}` : String(firmId);
      let group = groups.get(key);
      if (!group) {
        group = {
          firmId,
          title: recipient.carrierName || recipient.name,
          recipients: [],
          carrierNames: [],
        };
        groups.set(key, group);
      }
      group.recipients.push(recipient);
      for (const name of recipient.carrierNames ?? []) {
        if (!group.carrierNames.includes(name)) group.carrierNames.push(name);
      }
    }
    return Array.from(groups.values());
  };

  if (rfpCoverageTypes.length === 0) {
    return (
      <div ref={containerRef} className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">RFP Preview & Send</h3>
          <p className="text-muted-foreground">
            No RFP needed for selected coverage types
          </p>
        </div>

        <Card>
          <CardContent className="p-12 text-center">
            <Send className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h4 className="text-lg font-medium mb-2">No RFP Required</h4>
            <p className="text-muted-foreground">
              Your selected coverage types don't require RFP submission. Click Next to
              continue.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (rfpSent) {
    const uniqueCarriers = getUniqueCarriers();
    const sentGroups = sentByFirm();

    return (
      <div ref={containerRef} className="space-y-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-2xl font-bold mb-2">
            {sendFailures.length ? "RFP partly sent" : "RFP sent"}
          </h3>
          {/* The counts: this is the record of what left. */}
          <p className="text-muted-foreground">
            {[
              `${sentRecipients.length} email${sentRecipients.length === 1 ? "" : "s"}`,
              usingGa
                ? `${sentGroups.length} general agenc${sentGroups.length === 1 ? "y" : "ies"}`
                : `${uniqueCarriers.length} carrier${uniqueCarriers.length === 1 ? "" : "s"}`,
              sendFailures.length
                ? `${sendFailures.length} could not be delivered`
                : "all delivered",
            ].join(" · ")}
          </p>
        </div>

        {/* Say exactly who didn't get it — a silent partial send means a carrier
            never quotes and nobody notices until the comparison looks short. */}
        {sendFailures.length > 0 && (
          <Card className="border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
            <CardContent className="p-4">
              <h4 className="font-semibold text-sm text-red-900 dark:text-red-100 mb-2">
                Not delivered
              </h4>
              <ul className="space-y-1">
                {sendFailures.map((failure) => (
                  <li key={failure.email} className="text-xs text-red-800 dark:text-red-200">
                    <span className="font-mono">{failure.email}</span> — {failure.error ?? "unknown error"}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Emails sent</p>
                  <p className="text-xl font-semibold">{sentRecipients.length}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                  <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  {/* Through a GA the carrier count is what the GAs were ASKED
                      for, which is not the number of firms emailed. */}
                  <p className="text-sm text-muted-foreground">
                    {usingGa ? "General agencies" : "Carriers"}
                  </p>
                  <p className="text-xl font-semibold">
                    {usingGa ? sentGroups.length : uniqueCarriers.length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                  <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sent on</p>
                  <p className="text-xl font-semibold">{sentDate ? format(sentDate, "MMM d, yyyy") : "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Grouped by firm, with the same tint and mark as the review
                step. A flat address list never said who was asked for what. */}
            <div className="border-t pt-4 space-y-3">
              <h4 className="font-semibold">Who received it</h4>
              {sentGroups.map((group) => (
                <div key={group.title} className="overflow-hidden rounded-xl border">
                  <div className="flex items-center gap-2.5 border-b bg-muted/20 px-3 py-2.5">
                    {group.firmId !== null ? (
                      <GaMark id={group.firmId} name={group.title} />
                    ) : (
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{group.title}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {group.recipients.length} email
                        {group.recipients.length === 1 ? "" : "s"}
                        {group.carrierNames.length
                          ? ` · asked for ${group.carrierNames.length} carrier${
                              group.carrierNames.length === 1 ? "" : "s"
                            }`
                          : ""}
                      </span>
                    </span>
                    <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  </div>

                  <div className="space-y-1 px-3 py-2.5">
                    {group.recipients.map((recipient) => (
                      <div key={recipient.email} className="flex items-center gap-2 text-xs">
                        <span className="min-w-0 truncate font-medium">{recipient.name}</span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                          {recipient.email}
                        </span>
                      </div>
                    ))}

                    {group.carrierNames.length > 0 && (
                      <p className="border-t pt-2 text-[11px] leading-relaxed text-muted-foreground">
                        <span className="font-semibold uppercase tracking-wider">Asked to quote</span>
                        <br />
                        {group.carrierNames.join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold mb-1 text-green-900 dark:text-green-100">
                  What's next: let AI build the comparison
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                  {usingGa
                    ? "Your GA sends back a separate proposal per carrier. Drop them here — PDF, Word or Excel, even scanned ones."
                    : "As carriers reply, drop their proposals here — PDF, Word or Excel, even scanned ones."}{" "}
                  AI reads the rates and benefits out of each and builds a side-by-side
                  comparison you can edit, brand and share with the client. No re-typing.
                </p>
                <div className="flex flex-wrap gap-2">
                  {draftQuoteId && (
                    <Button asChild variant="default">
                      <Link href={`/quoting/rfp/${draftQuoteId}/proposals`}>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Compare proposals with AI
                      </Link>
                    </Button>
                  )}
                  <Button onClick={handleComplete} variant="outline">
                    Back to quotes
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-lg font-semibold">Review &amp; send</h3>
        <p className="text-sm text-muted-foreground">
          Select recipients and review the RFP email before sending
        </p>
      </div>

      <ConsolidatedRFPEmail
        availableContacts={availableContacts}
        selectedContactIds={selectedContactIds}
        onToggleContact={handleToggleContact}
        customRecipients={visibleCustomRecipients}
        onAddCustomRecipient={handleAddCustomRecipient}
        onRemoveCustomRecipient={handleRemoveCustomRecipient}
        onContactCreated={() => refetchDirectoryContacts()}
        carriers={carriers}
        rfpCarriers={rfpCarriers}
        usingGa={usingGa}
        carrierNamesByRecipient={carrierNamesByRecipient}
        // Needs a saved draft to hang the files on, which is also why they
        // survive leaving the step and coming back.
        attachmentsSlot={
          draftQuoteId ? (
            <RfpAttachments
              quoteId={draftQuoteId}
              coverageTypes={rfpCoverageTypes}
              currentMessage={customMessage}
              onAddLink={(name, url) =>
                setCustomMessage((current) =>
                  // Appended rather than replacing: the broker may have written
                  // their own note already.
                  `${current.trim() ? `${current.trim()}\n\n` : ""}${name}: ${url}`,
                )
              }
            />
          ) : null
        }
        carriersWithoutContacts={carriersWithoutContacts}
        senderName={emailSettings?.fromName}
        senderEmail={emailSettings?.fromEmail}
        replyTo={replyTo}
        onReplyToChange={setReplyTo}
        perRecipientMessages={perRecipientMessages}
        onPerRecipientMessageChange={(recipientId, value) =>
          setPerRecipientMessages((prev) => ({ ...prev, [recipientId]: value }))
        }
        coverageTypes={rfpCoverageTypes}
        rfpParameters={quoteData.rfpParameters}
        companyName={quoteData.selectedEntity?.name || "Unknown Company"}
        effectiveDate={quoteData.effectiveDate || new Date()}
        employeeCount={quoteData.selectedEmployees.length}
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        // Straight into quoteData, so it rides the existing draft save and the
        // server reads the same three values back out of wizardState at send.
        respondBy={quoteData.rfpRespondBy ?? null}
        emailGreeting={quoteData.rfpEmailGreeting ?? null}
        emailIntro={quoteData.rfpEmailIntro ?? null}
        emailClosing={quoteData.rfpEmailClosing ?? null}
        emailSignature={quoteData.rfpEmailSignature ?? null}
        sender={{
          name: [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || null,
          email: profile?.contactEmail || profile?.email || null,
          phone: profile?.phone || null,
          agencyName: profile?.agencyName || null,
          agencyLicenseNumber: profile?.agencyLicenseNumber || null,
        }}
        agencyLogo={profile?.agencyLogo ?? null}
        onEditingMessageChange={setIsEditingMessage}
        onTemplateChange={(patch) =>
          updateQuoteData({
            ...(patch.respondBy !== undefined && { rfpRespondBy: patch.respondBy }),
            ...(patch.emailGreeting !== undefined && { rfpEmailGreeting: patch.emailGreeting }),
            ...(patch.emailIntro !== undefined && { rfpEmailIntro: patch.emailIntro }),
            ...(patch.emailClosing !== undefined && { rfpEmailClosing: patch.emailClosing }),
            ...(patch.emailSignature !== undefined && { rfpEmailSignature: patch.emailSignature }),
          })
        }
      />

      {/* Nothing on the parameters step is required — brokers legitimately send
          open RFPs. But an unfilled field is printed to carriers as "Not
          specified", and each one then quotes to its own defaults, so the
          proposals that come back don't line up. Say so here, where they can
          still go back, and let them send anyway. */}
      {parameterGaps.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm text-amber-900 dark:text-amber-100">
                  {gapCount} item{gapCount === 1 ? "" : "s"} not specified
                </h4>
                <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5 mb-2">
                  {usingGa
                    ? 'Carriers will see "Not specified" for these and quote to their own defaults, which makes the proposals your GA sends back harder to compare. You can still send.'
                    : 'Carriers will see "Not specified" for these and quote to their own defaults, which makes the proposals harder to compare. You can still send.'}
                </p>
                <ul className="space-y-1">
                  {parameterGaps.map((gap) => (
                    <li key={gap.coverageType} className="text-xs text-amber-900 dark:text-amber-200">
                      <span className="font-medium">{gap.label}:</span> {gap.missing.join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sending belongs in the wizard footer with every other step's action.
          Inside the scroll area it either floats over the preview or scrolls
          out of reach, and this step is a long read. */}
      {footerSlot &&
        createPortal(
          <>
            <div className="min-w-0 text-right">
              <div className="flex items-center justify-end gap-1.5">
                {readyToSend ? (
                  <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-500" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-500" />
                )}
                <span className="text-sm font-medium">
                  {readyToSend ? "Ready to send" : blockingReason}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {totalRecipients} recipient{totalRecipients !== 1 ? "s" : ""} ·{" "}
                {rfpCoverageTypes.length} coverage type{rfpCoverageTypes.length !== 1 ? "s" : ""}
                {gapCount > 0 && ` · ${gapCount} unspecified`}
              </p>
            </div>
            {/* Same rules as the real send, because it builds the real email:
                with no recipient there is nothing to preview. */}
            <Button
              variant="outline"
              disabled={!readyToSend || isSending || sendingTest}
              onClick={handleSendTest}
            >
              {sendingTest ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Email me a test
            </Button>
            <Button disabled={!readyToSend || isSending || sendingTest} onClick={handleSendRFP}>
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {totalRecipients}{" "}
                  {/* Recipients are people, and one GA can have several of
                      them, so counting "GAs" here would be wrong. */}
                  {usingGa
                    ? `GA contact${totalRecipients === 1 ? "" : "s"}`
                    : `carrier${totalRecipients === 1 ? "" : "s"}`}
                </>
              )}
            </Button>
          </>,
          footerSlot,
        )}
    </div>
  );
}
